import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchConexiones, revocarConexion } from '../api';
import { Empty, ErrorBox, Loading, RelativeTime, Section, Table } from '../components/ui';

const PERMISO: Record<string, string> = {
  'tasks:read': 'leer',
  'tasks:write': 'cambiar estado y comentar',
  offline_access: 'sin volver a preguntar',
};

/**
 * Las conexiones de Claude que tiene abiertas quien mira: qué aplicación, con qué permisos
 * y hasta cuándo. Cortar una revoca al momento todos sus tokens; para volver a usarla hay
 * que conectar de nuevo desde Claude.
 *
 * Solo las PROPIAS. Las de los demás se ven en el registro de auditoría (acciones oauth_*).
 */
export function ConexionesPage(): JSX.Element {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['oauth-conexiones'], queryFn: fetchConexiones });
  const cortar = useMutation({
    mutationFn: revocarConexion,
    onSuccess: () => client.invalidateQueries({ queryKey: ['oauth-conexiones'] }),
  });

  if (query.isPending) return <Loading />;
  if (query.isError) return <ErrorBox error={query.error} />;

  return (
    <>
      <h1 className="mb-1 text-lg font-semibold">Conexiones</h1>
      <p className="mb-6 text-[13px] text-[var(--color-ink-muted)]">
        Aplicaciones a las que has dado acceso a tus tareas (Claude, mediante el conector del MCP).
        Los tokens estáticos de Claude Code no aparecen aquí: los gestiona un administrador en el
        servidor.
      </p>
      <Section title="Activas">
        {query.data.conexiones.length === 0 ? (
          <Empty>No tienes ninguna conexión activa.</Empty>
        ) : (
          <Table head={['Aplicación', 'Permisos', 'Desde', 'Caduca', '']}>
            {query.data.conexiones.map((c) => (
              <tr
                key={c.familia}
                className="border-b border-[var(--color-border-subtle)] last:border-0"
              >
                <td className="px-3 py-2">
                  {new URL(c.cliente).hostname}
                  {c.nombre && <span className="text-[var(--color-ink-muted)]"> · {c.nombre}</span>}
                </td>
                <td className="px-3 py-2">{c.scopes.map((s) => PERMISO[s] ?? s).join(', ')}</td>
                <td className="px-3 py-2">
                  <RelativeTime iso={c.creada} />
                </td>
                <td className="px-3 py-2">{c.caduca.slice(0, 10)}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    disabled={cortar.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          '¿Cortar esta conexión? Claude dejará de tener acceso al momento.',
                        )
                      ) {
                        cortar.mutate(c.familia);
                      }
                    }}
                    className="rounded border border-[var(--color-border-subtle)] px-2 py-1 text-[12px] disabled:opacity-50"
                  >
                    Cortar
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        )}
        {cortar.isError && (
          <div className="mt-3">
            <ErrorBox error={cortar.error} />
          </div>
        )}
      </Section>
    </>
  );
}
